import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { EVENTS } from "../../data/events";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT = MONTHS.map((m) => m.slice(0, 3));
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const CULTURE_PATH = "/whats-good";

export const eventSlug = (e) => `${e.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${e.start}`;

const d = (s) => { const [y, m, dd] = s.split("-"); return new Date(+y, +m - 1, +dd); };
const ymd = (dt) => `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getDate()).padStart(2, "0")}`;
const today = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

export const upcoming = (filter = "all") => {
  const t = today();
  const soonEnd = new Date(t); soonEnd.setDate(t.getDate() + 2);
  const match = (e) => filter === "all" || (filter === "soon" ? d(e.start) <= soonEnd : e.cat === filter);
  return EVENTS.filter((e) => d(e.end) >= t && match(e)).sort((a, b) => d(a.start) - d(b.start));
};

export const groupByMonth = (list) => {
  const t = today();
  const groups = [];
  list.forEach((e) => {
    const a = d(e.start);
    const label = a < t ? "Happening now" : MONTHS[a.getMonth()];
    const key = a < t ? "now" : `${a.getFullYear()}-${a.getMonth()}`;
    if (groups[groups.length - 1]?.key !== key) groups.push({ key, label, items: [] });
    groups[groups.length - 1].items.push(e);
  });
  return groups;
};

const dateParts = (e) => {
  const a = d(e.start), b = d(e.end);
  if (a < today()) return { top: "Through", big: `${SHORT[b.getMonth()]} ${b.getDate()}`, sub: "" };
  const sub = e.start === e.end ? DAYS[a.getDay()] : a.getMonth() === b.getMonth() ? `to ${b.getDate()}` : `to ${SHORT[b.getMonth()]} ${b.getDate()}`;
  return { top: SHORT[a.getMonth()], big: a.getDate(), sub };
};

const gcal = (e) => {
  const end = d(e.end); end.setDate(end.getDate() + 1);
  const details = [e.time, e.note].filter(Boolean).join(". ");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(e.title)}&dates=${ymd(d(e.start))}/${ymd(end)}&location=${encodeURIComponent(`${e.where}, Dallas, TX`)}&details=${encodeURIComponent(`${details}${details ? "\n\n" : ""}Via Kristin McIntyre · kristinnmac.com`)}`;
};

export const EventDate = ({ e, compact = false }) => {
  const p = dateParts(e);
  return (
    <div className="font-[Montserrat,Arial,sans-serif] uppercase">
      <span className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] text-[#5E5E5E]">{p.top}</span>
      <b className={`block font-medium leading-none mt-1.5 ${compact ? "text-[26px]" : "text-[26px] sm:text-[34px]"} ${typeof p.big === "string" ? "!text-[20px] sm:!text-[24px]" : ""}`}>{p.big}</b>
      {p.sub && <span className="block text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] mt-1.5 text-[#5E5E5E]">{p.sub}</span>}
    </div>
  );
};

const Note = ({ text, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <p onClick={() => setOpen(!open)} className={`km-serif italic text-[16px] sm:text-[19px] leading-[1.45] mt-1 sm:mt-1.5 max-sm:cursor-pointer ${open ? "" : "max-sm:line-clamp-2"}`} data-testid={`event-note-${i}`}>{text}</p>
  );
};

const share = async (e) => {
  const p = dateParts(e);
  const url = `${window.location.origin}${CULTURE_PATH}#${eventSlug(e)}`;
  const text = `${e.title} · ${p.top} ${p.big}${p.sub && p.top !== "Through" ? ` (${p.sub})` : ""} at ${e.where}. Found on Kristin McIntyre’s Culture Calendar:`;
  if (navigator.share) {
    try { await navigator.share({ title: e.title, text, url }); } catch { /* closed by user */ }
    return;
  }
  try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied. Paste it to a friend."); }
  catch { window.prompt("Copy this link:", url); }
};

const action = "km-eyebrow text-[10px] sm:text-[11px] text-[#141414] hover:text-[#2C4A3B] border-b border-[#2C4A3B] pt-2.5 pb-1 sm:pt-3.5 whitespace-nowrap transition-colors";

export const EventRow = ({ e, i }) => (
  <li id={eventSlug(e)} className="scroll-mt-[150px] sm:scroll-mt-[110px] grid grid-cols-[56px_minmax(0,1fr)] sm:grid-cols-[120px_minmax(0,1fr)_auto] gap-x-4 sm:gap-x-6 items-start py-5 sm:py-7 border-b border-[#E2E2E2]" data-testid={`event-${i}`}>
    <EventDate e={e} />
    <div>
      <p className="km-eyebrow text-[10px] sm:text-[11px] text-[#2C4A3B]">{e.cat} · {e.hood}</p>
      <h4 className="km-serif text-[22px] sm:text-[30px] font-medium leading-[1.15] mt-1 sm:mt-1.5">{e.title}</h4>
      <p className="km-serif text-[16px] sm:text-[19px] leading-[1.4] sm:leading-[1.45] text-[#5E5E5E] mt-1 sm:mt-1.5">{e.where}{e.time && ` · ${e.time}`}</p>
      {e.note && <Note text={e.note} i={i} />}
    </div>
    <div className="col-start-2 sm:col-start-3 flex items-start gap-6 sm:flex-col sm:items-end sm:gap-1">
      <a href={gcal(e)} target="_blank" rel="noopener noreferrer" aria-label={`Add ${e.title} to Google Calendar`} className={action} data-testid={`event-add-${i}`}>Add to calendar</a>
      <button type="button" onClick={() => share(e)} aria-label={`Share ${e.title}`} className={`${action} flex items-center gap-1.5`} data-testid={`event-share-${i}`}><Share2 size={12} strokeWidth={1.6} /> Share</button>
    </div>
  </li>
);

export default function CultureTeaser() {
  const next = upcoming().slice(0, 3);
  if (!next.length) return null;
  return (
    <section className="py-12 sm:py-20 px-6 md:px-12 lg:px-16 bg-white" data-testid="km-culture-teaser">
      <div className="reveal max-w-[1180px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-10">
          <div>
            <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-3 text-[#2C4A3B]">The Culture Calendar</p>
            <h2 className="km-serif text-[34px] sm:text-5xl leading-[1.05] text-[#141414]">What’s good in <em>the neighborhood?</em></h2>
          </div>
          <div className="hidden sm:block"><Link to={CULTURE_PATH} className="km-btn-outline gap-2" data-testid="culture-teaser-cta-desktop">See the Full Calendar <ArrowRight size={14} strokeWidth={1.5} /></Link></div>
        </div>
        <ul className="grid sm:grid-cols-3 border-t border-[#141414]">
          {next.map((e, i) => (
            <li key={e.title + e.start} className="flex gap-4 py-5 sm:py-7 sm:pr-6 border-b border-[#E2E2E2] sm:border-b-0 sm:[&:not(:first-child)]:pl-6 sm:[&:not(:first-child)]:border-l" data-testid={`culture-teaser-event-${i}`}>
              <div className="w-[64px] shrink-0"><EventDate e={e} compact /></div>
              <div className="min-w-0">
                <p className="km-eyebrow text-[10px] text-[#2C4A3B]">{e.cat}</p>
                <p className="km-serif text-[21px] sm:text-[23px] leading-[1.15] mt-1">{e.title}</p>
                <p className="km-serif text-[16px] text-[#5E5E5E] mt-1">{e.hood}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="sm:hidden mt-7"><Link to={CULTURE_PATH} className="km-btn-dark gap-2 w-full" data-testid="culture-teaser-cta">See the Full Calendar <ArrowRight size={14} strokeWidth={1.5} /></Link></div>
      </div>
    </section>
  );
}
